import React from 'react';
import { IRegistroCircularizacao } from '../interfaces/ICircularizacao';
import { gerarItensSolicitados } from '../utils/categoriaConteudo';

interface Props {
  registros: IRegistroCircularizacao[];
  empresaAuditada: string;
  dataBase: string;
  emailRetornoEquipe: string;
  nomeSignatario: string;
  onFechar: () => void;
}

const formatarDataPtBr = (iso?: string) => {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.split('-');
  return dia ? `${dia}/${mes}/${ano}` : iso;
};

// Seção 10.1/10.2 — "Imprimir Selecionadas" / "Permitir impressão em lote" / "Separar
// adequadamente os documentos" (uma carta por página impressa, via quebra de página em CSS) /
// "Não preparar documentos com campos obrigatórios pendentes" — cartas ainda não PRONTAs são
// avisadas e excluídas do lote, nunca impressas incompletas.
const ImpressaoEmLoteModal: React.FC<Props> = ({
  registros,
  empresaAuditada,
  dataBase,
  emailRetornoEquipe,
  nomeSignatario,
  onFechar,
}) => {
  const prontas = registros.filter((r) => r.situacaoPreparacaoCarta === 'PRONTA');
  const ignoradas = registros.filter((r) => r.situacaoPreparacaoCarta !== 'PRONTA');

  return (
    <div className='circ-modal-backdrop' onMouseDown={(e) => e.target === e.currentTarget && onFechar()}>
      <div className='circ-modal circ-modal-lg'>
        <div className='circ-modal-header'>
          <h4>Impressão em Lote — {prontas.length} carta{prontas.length === 1 ? '' : 's'}</h4>
          <button type='button' className='btn btn-sm btn-icon btn-light' aria-label='Fechar' onClick={onFechar}>
            <i className='bi bi-x-lg' />
          </button>
        </div>
        <div className='circ-modal-body' style={{ background: '#eef0f4' }}>
          {ignoradas.length > 0 && (
            <div className='circ-aviso-revisao mb-3' style={{ background: '#fff8e8', borderColor: '#ffe29a', color: '#8a5a00' }}>
              <i className='bi bi-exclamation-triangle-fill' />
              <div>
                {ignoradas.length} empresa{ignoradas.length === 1 ? '' : 's'} não entrará
                {ignoradas.length === 1 ? '' : 'ão'} no lote por ainda não ter carta pronta:{' '}
                {ignoradas.map((r) => r.razaoSocialExibicao).join(', ')}.
              </div>
            </div>
          )}

          {prontas.length === 0 ? (
            <div className='circ-empty-state'>
              <i className='bi bi-printer circ-empty-state-icon' />
              <div className='circ-empty-state-title'>Nenhuma carta pronta para imprimir</div>
              <div className='circ-empty-state-texto'>Prepare as cartas das empresas selecionadas antes de imprimir em lote.</div>
            </div>
          ) : (
            <div className='circ-carta-lote-wrap'>
            {prontas.map((registro, idx) => {
              const contatoPrincipal = registro.contatos.find((c) => c.contatoPrincipal) ?? registro.contatos[0];
              const itens = gerarItensSolicitados(registro.categoria);
              return (
                <div
                  key={registro.id}
                  className='circ-carta-a4 circ-carta-lote-item'
                  style={{ width: 'auto', minHeight: 'auto', boxShadow: 'none', marginBottom: idx < prontas.length - 1 ? 24 : 0 }}
                >
                  <div className='circ-carta-cabecalho'>
                    <div>
                      <div className='circ-carta-titulo'>Carta de Circularização</div>
                      <div style={{ fontSize: 11.5, color: '#6b7280' }}>{empresaAuditada}</div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 11.5 }}>
                      <div>Data de emissão: {new Date().toLocaleDateString('pt-BR')}</div>
                      <div>Data-base: {formatarDataPtBr(dataBase)}</div>
                    </div>
                  </div>
                  <div style={{ marginBottom: 16, fontSize: 12.5 }}>
                    <strong>Destinatário:</strong> {registro.razaoSocialExibicao} — {registro.cnpj}
                    <br />
                    {contatoPrincipal && (
                      <>
                        <strong>Aos cuidados de:</strong> {contatoPrincipal.nomeCompleto} ({contatoPrincipal.emailPrincipal})
                      </>
                    )}
                  </div>
                  <div className='circ-carta-corpo'>
                    <p>
                      Prezados, no âmbito dos trabalhos de auditoria independente com data-base de{' '}
                      {formatarDataPtBr(dataBase)}, solicitamos a confirmação das informações abaixo, mantidas com{' '}
                      {empresaAuditada} na data-base mencionada:
                    </p>
                    <ul style={{ paddingLeft: 20, marginBottom: 10 }}>
                      {itens.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                    <p>
                      Solicitamos que a resposta seja encaminhada até {formatarDataPtBr(registro.prazoResposta)},
                      preferencialmente por meio do e-mail {emailRetornoEquipe}.
                    </p>
                  </div>
                  <div className='circ-carta-assinatura'>
                    <div className='circ-carta-assinatura-linha' />
                    {nomeSignatario}
                    <br />
                    <span style={{ fontSize: 11, color: '#6b7280' }}>Responsável pelos trabalhos de auditoria</span>
                  </div>
                </div>
              );
            })}
            </div>
          )}
        </div>
        <div className='circ-modal-footer'>
          <button type='button' className='btn btn-sm btn-light' onClick={onFechar}>
            Fechar
          </button>
          <button type='button' className='btn btn-sm btn-primary' disabled={prontas.length === 0} onClick={() => window.print()}>
            <i className='bi bi-printer me-1' /> Imprimir {prontas.length} Carta{prontas.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImpressaoEmLoteModal;
